import {Component, computed, CUSTOM_ELEMENTS_SCHEMA, effect, EventEmitter, Input, Output, signal} from '@angular/core';
import {CookieService} from "@/app/services/cookie.service";
import {TranslatePipe} from '@ngx-translate/core';
import {SiteCardComponent} from '../site-card/site-card.component';
import {CommonModule, PercentPipe} from '@angular/common';
import {EventService} from '@/app/services/event.service';
import {ColourBin, SiteCardSpec} from '@/app/model/common';
import {applyCss} from '@/app/stylesFormat/stylesFormat';
import {MatIcon} from '@angular/material/icon';
import {getTzAbbrForce} from '@/app/common/common';
import {TranslateService} from "@ngx-translate/core";
import {EventAPI, Event} from "@/app/model/interface/event";
import {EventNodeAPI} from "@/app/model/interface/event-node";


@Component({
    selector: 'app-portfolio-card',
    imports: [CommonModule, SiteCardComponent, MatIcon, TranslatePipe],
    templateUrl: './portfolio-card.component.html',
    styleUrl: './portfolio-card.component.scss',
    schemas: [
        CUSTOM_ELEMENTS_SCHEMA,
    ],
    providers: [PercentPipe]
})
export class PortfolioCardComponent {
    @Input() event: Event;
    @Input() index: number;
    @Output() eventNodeSelected = new EventEmitter<void>();
    public programName: string;
    public sitesCount: number;
    public targetReduction: {
        value: string | undefined,
        uom: string | undefined
    };
    public showSites: boolean = false;
    public isLoading = signal<boolean>(false);
    public eventNodes: EventNodeAPI[];
    public siteCardsSpec: SiteCardSpec[] = [];
    public performance: {
        class: string,
        percentage: string
    };
    public startTimeString: string;
    public endTimeString: string;
    public selectedEvent: EventAPI | null;
    public selectedEventNode: EventNodeAPI | null;
    private locale: string;

    public isEventSelected = computed<boolean>(() => {
        return this.event === this.eventService.selectedEvent();
    });

    constructor(
        private readonly eventService: EventService,
        private readonly percentPipe: PercentPipe,
        private readonly cookieService: CookieService,
        private readonly translate: TranslateService,
    ) {
        effect(() => {
            const globalSelectedNode = this.eventService.selectedEventNodeSource();
            const selectedEvent = this.eventService.selectedEvent();
            const isThisEventSelected = selectedEvent?.event_id === this.event?.event_id;

            if (isThisEventSelected && globalSelectedNode) {
                this.selectedEventNode = globalSelectedNode;
            }

            if (this.eventNodes) {
                this.siteCardsSpec = this.createSiteCardSpec(this.eventNodes, this.selectedEventNode);
            }

            const eventId = this.event?.event_id;
            if (!eventId) return;

            // If the user already interacted with this card's expansion, always respect stored state.
            if (this.eventService.hasSidebarEventExpansionBeenTouched(eventId)) {
                this.showSites = this.eventService.isSidebarEventExpanded(eventId);
                return;
            }

            // Initial-load behavior: only auto-expand if this is the selected event
            // Once expanded, mark it as touched so it won't auto-collapse when selecting a different event
            const selectedEventId = selectedEvent?.event_id;
            if (selectedEventId === eventId) {
                this.showSites = true;
                this.eventService.setSidebarEventExpanded(eventId, true);
                this.eventService.markSidebarEventExpansionTouched(eventId);
            }
        })
    }

    private safeIntlLocale(rawLocale: string | null | undefined): string {
        if (!rawLocale) return 'en-US';
        try {
            Intl.getCanonicalLocales(rawLocale);
            return rawLocale;
        } catch {
            return 'en-US';
        }
    }

    setCardInfo() {
        this.locale = this.cookieService.getI18NLocale();
        this.selectedEvent = this.eventService.selectedEvent();

        this.initializeExpansionState();
        this.loadEventNodesIfNeeded();
        this.setBasicCardProperties();
        this.setTimeStrings();
        this.setPerformance();

        if (this.event === this.selectedEvent) {
            this.showSites = true;
        }
    }

    private initializeExpansionState(): void {
        if (this.eventService.hasSidebarEventExpansionBeenTouched(this.event.event_id)) {
            this.showSites = this.eventService.isSidebarEventExpanded(this.event.event_id);
        } else {
            const selectedEventId = this.selectedEvent?.event_id;
            if (selectedEventId === this.event.event_id) {
                this.showSites = true;
                this.eventService.setSidebarEventExpanded(this.event.event_id, true);
                this.eventService.markSidebarEventExpansionTouched(this.event.event_id);
            } else {
                this.showSites = false;
            }
        }
    }

    private loadEventNodesIfNeeded(): void {
        const isExpandedWithoutNodes = this.showSites && !this.eventNodes;
        const isSelectedEvent = this.selectedEvent?.event_id === this.event.event_id;

        if (!isExpandedWithoutNodes && !isSelectedEvent) {
            return;
        }

        if (isExpandedWithoutNodes) {
            this.isLoading.set(true);
        }

        const subscribeToRealtimeUpdates = this.event.event_progress_status !== 'AFTER';
        this.eventService.getEventNodes(this.event.event_id, subscribeToRealtimeUpdates).subscribe((nodes) => {
            this.eventNodes = nodes;

            if (isSelectedEvent) {
                this.selectedEventNode = this.resolveSelectedNodeForSelectedEvent(nodes);
            }
            this.siteCardsSpec = this.createSiteCardSpec(nodes, this.selectedEventNode);

            if (isExpandedWithoutNodes) {
                this.isLoading.set(false);
            }
        });
    }

    private resolveSelectedNodeForSelectedEvent(nodes: EventNodeAPI[]): EventNodeAPI {
        const currentNode = this.eventService.selectedEventNodeSource();

        if (!currentNode) {
            this.eventService.selectedEventNodeSource.set(nodes[0]);
            return nodes[0];
        }

        const currentNodeBelongsToEvent = nodes.some(
            node => node.event_node_id === currentNode.event_node_id
        );

        if (!currentNodeBelongsToEvent) {
            this.eventService.selectedEventNodeSource.set(nodes[0]);
            return nodes[0];
        }

        return currentNode;
    }

    private setBasicCardProperties(): void {
        this.programName = this.event.program.name;
        this.sitesCount = this.event.len_total_event_nodes;
        const formattedReduction = this.formatPowerValue(
            this.event.sum_expected_capacity_value,
            this.event.sum_expected_capacity_uom
        );
        this.targetReduction = {
            value: formattedReduction.value,
            uom: formattedReduction.uom
        };
    }

    private setTimeStrings(): void {
        const {full_time_zone, program_time_zone_abbr, event_start_dttm_utc, event_end_dttm_utc} = this.event;
        const effectiveTimeZone = full_time_zone || 'UTC';
        const timeFormatter = this.createTimeFormatter(effectiveTimeZone);
        const uiLocale = this.safeIntlLocale(this.locale);

        const eventStart = new Date(event_start_dttm_utc);
        const startTzAbbr = this.getTimezoneAbbreviation(full_time_zone, program_time_zone_abbr, eventStart);
        this.startTimeString = this.formatDateTimeString(eventStart, timeFormatter, startTzAbbr, uiLocale, effectiveTimeZone);

        if (event_end_dttm_utc) {
            const eventEnd = new Date(event_end_dttm_utc);
            const endTzAbbr = this.getTimezoneAbbreviation(full_time_zone, program_time_zone_abbr, eventEnd);
            this.endTimeString = this.formatDateTimeString(eventEnd, timeFormatter, endTzAbbr, uiLocale, effectiveTimeZone);
        } else {
            this.endTimeString = this.translate.instant('DISPATCH_LIST.PORTFOLIO_CARD.WINDOW.NONE');
        }
    }

    private getTimezoneAbbreviation(fullTimeZone: string, programTimeZoneAbbr: string, date: Date): string {
        let timeZone = programTimeZoneAbbr;
        if (timeZone.startsWith('+') || timeZone.startsWith('-')) {
            timeZone = `GMT ${timeZone}`;
        }
        return getTzAbbrForce(fullTimeZone || timeZone, date);
    }

    private createTimeFormatter(timeZone: string): Intl.DateTimeFormat {
        const timeOptions: Intl.DateTimeFormatOptions = {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
            timeZone,
        };
        const locale = this.safeIntlLocale(this.locale);
        return new Intl.DateTimeFormat(locale, timeOptions);
    }

    private formatDateTimeString(date: Date, timeFormatter: Intl.DateTimeFormat, tzAbbr: string, uiLocale: string, timeZone: string): string {
        const formattedTime = `${timeFormatter.format(date)} ${tzAbbr}`;
        const dayLabel = this.getDayLabel(date, uiLocale, timeZone);
        return `${dayLabel}, ${formattedTime}`;
    }

    private getDayLabel(date: Date, uiLocale: string, timeZone: string): string {
        if (this.isTodayInTimezone(date, timeZone)) {
            return this.translate.instant('DAYS.TODAY');
        }
        return this.getWeekdayLabel(date, uiLocale, timeZone);
    }

    private getWeekdayLabel(date: Date, locale: string, timeZone: string): string {
        if (this.locale === 'ja-jp') {
            const weekdayKey = new Intl.DateTimeFormat('en-US', { weekday: 'long', timeZone }).format(date).toUpperCase();
            return this.translate.instant(`WEEK.${weekdayKey}`);
        }
        return new Intl.DateTimeFormat(locale, { weekday: 'long', timeZone }).format(date);
    }

    private isTodayInTimezone(date: Date, timeZone: string): boolean {
        return this.getDateKey(date, timeZone) === this.getDateKey(new Date(), timeZone);
    }

    private getDateKey(date: Date, timeZone: string): string {
        return new Intl.DateTimeFormat('en-CA', {
            timeZone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        }).format(date);
    }

    private setPerformance(): void {
        const isRangeLoadDropTo = this.event.product?.target_type === 'RANGE';
        this.performance = {
            class: isRangeLoadDropTo ? 'performance-wut' : this.detectPerformanceLabel(this.event),
            percentage: isRangeLoadDropTo
                ? '--'
                : (this.percentPipe.transform(this.event.event_performance_percent / 100) ?? '?')
        };
    }

    ngOnInit() {
        this.setCardInfo();
    }

    toggleSitesList() {
        const nextExpanded = !this.showSites;

        if (nextExpanded && !this.eventNodes) {
            this.isLoading.set(true);
            const subscribeToRealtimeUpdates = this.event.event_progress_status !== 'AFTER';
            this.eventService.getEventNodes(this.event.event_id, subscribeToRealtimeUpdates).subscribe((nodes) => {
                this.eventNodes = nodes;
                this.siteCardsSpec = this.createSiteCardSpec(nodes, this.selectedEventNode);
                this.isLoading.set(false);
            });
        }

        this.showSites = nextExpanded;

        // Persist the user's explicit interaction so polling won't re-open a closed card.
        this.eventService.markSidebarEventExpansionTouched(this.event.event_id);
        this.eventService.setSidebarEventExpanded(this.event.event_id, this.showSites);

        setTimeout(() => applyCss());
    }

    createSiteCardSpec(eventNodes: EventNodeAPI[], selectedNode: EventNodeAPI | null) {
        const isThisEventSelected = this.eventService.selectedEvent()?.event_id === this.event.event_id;

        return eventNodes.map((node, i) => {
            const isChecked = isThisEventSelected && selectedNode?.event_node_id === node.event_node_id;
            const formattedReduction = this.formatPowerValue(node.expected_capacity_value, node.expected_capacity_uom);
            return {
                index: this.index,
                event_node_index: i,
                isChecked,
                isDisabled: false,
                id: node.event_node_id,
                name: node.event_node_id,
                value: node.event_node_id,
                event_id: node.event_id,
                registration_id: node.registration_id,
                status: "default",
                leftLabelText: "",
                leftSubLabelText: "",
                isCurrent: true,
                siteName: node.site_name,
                reduction: `${formattedReduction.value} ${formattedReduction.uom}`,
                performance: this.event.product?.target_type === 'RANGE' && node.registration_type === 'LOAD_DROP_TO'
                    ? null
                    : node.perfPercentage / 100,
                performanceLabel: this.event.product?.target_type === 'RANGE' && node.registration_type === 'LOAD_DROP_TO'
                    ? 'performance-wut'
                    : this.detectPerformanceLabel(node),
                registration: node.registration,
                enableSiteDetails: (node.site_to_registration_map?.length ?? 0) > 1,
                onClickAction: () => this.selectEventNode(this.event, node)
            }
        });
    }

    detectPerformanceLabel(e: EventNodeAPI | EventAPI): string {
        const classMap: { [key: string]: ColourBin } = {
            UNDER_PERFORMING: 'bad',
            PERFORMING_NEAR_EXPECTATION: 'meh',
            PERFORMING: 'gud',
            NO_PERF_NUMBERS: 'wut',
            ONLY_TARGET_AVAILABLE: 'wut',
            ONLY_PERF_AVAILABLE: 'wut',
        };
        const status = e.average_performance_status ?? e.last_current_performance_status;
        return `performance-${status ? classMap[status] : 'wut'}`;
    }

    private formatPowerValue(value: number | null | undefined, uom: string | null | undefined): { value: string; uom: string } {
        const preferredUom = this.event.product?.prez_conf?.prefered_prez_demand_uom ?? uom ?? 'kW';
        const normalizedUom = preferredUom.toLowerCase() === 'kw' ? 'kW' : 'MW';
        const isKw = normalizedUom === 'kW';
        const fractionDigits = isKw ? 0 : 3;
        const normalizedValue = Number.isFinite(value) ? (isKw ? Number(value) : Number(value) / 1000) : 0;
        const locale = this.safeIntlLocale(this.locale);

        return {
            value: Intl.NumberFormat(locale, {
                minimumFractionDigits: fractionDigits,
                maximumFractionDigits: fractionDigits,
            }).format(normalizedValue),
            uom: normalizedUom
        };
    }

    selectEventNode(event: Event, eventNode: EventNodeAPI) {
        this.eventService.selectedEventNodeSource.set(eventNode);
        this.eventService.selectedEvent.set(event)
        this.eventService.setLoadEvents.set(true);
        this.eventNodeSelected.emit();
        if (this.eventNodes) {
            this.siteCardsSpec = this.siteCardsSpec.map(
                item => item.id === eventNode.event_node_id ? {...item, isChecked: true} : {...item, isChecked: false}
            )
        }
    }
}
